import { getMasterChartData } from '@/api/charts'
import { ComparisonSelection } from '@/components/ui/charts/types'
import { toast } from '@/hooks/useToast'
import { Interval } from '@/types/clarity/metrics/sales'
import {
  dateToString,
  formatNumber,
  getMinimumAvailableTime,
  isDateRangeValidForInterval,
} from '@/utils'
import { EChartsOption } from 'echarts'
import { useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'

const DOLLAR_METRICS = [
  'adns',
  'aov',
  'unitLaborCost',
  'spend',
  'ptiImpact',
  'netSalesPnL',
  // pnl
  'salesAmount',
]
const PERCENTAGE_METRICS = [
  'discounts',
  'laborRatio',
  'utilization',
  'ptiPercentage',
  // pnl
  'cogsAmount',
  'storeLaborAmount',
  'opExAmount',
  'rentAndOccupancyAmount',
  'overheadAmount',
  'ebitdaAmount',
]

export default function useMasterChart(
  dateRange: DateRange | undefined,
  intervalValue: Interval,
  setIntervalValue: (interval: Interval) => void,
  selectedBranch: string | undefined,
  selectedComparison: ComparisonSelection | null | undefined,
  typeFilter: string,
  defaultChartOptions: EChartsOption,
  setLineChartOptions: (options: EChartsOption) => void
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchChartData = useMemo(() => {
    return async () => {
      if (!dateRange) {
        return
      }

      const isValid = isDateRangeValidForInterval(
        dateRange,
        intervalValue,
        selectedComparison
      )
      if (!isValid) {
        setIntervalValue(getMinimumAvailableTime(dateRange, selectedComparison))
        return
      }

      try {
        const branchNames =
          selectedComparison?.type === 'branches'
            ? [selectedComparison.value.split(',')[0]]
            : [selectedBranch]

        const chartData = await getMasterChartData({
          startDate: dateToString(dateRange, 'from'),
          endDate: dateToString(dateRange, 'to'),
          branchNames: branchNames.filter(
            (branch): branch is string => branch !== undefined
          ),
          interval: intervalValue,
          dataName: typeFilter,
          type: selectedComparison?.type,
          value:
            selectedComparison?.type === 'branches'
              ? selectedComparison?.value?.split(',')[1]
              : selectedComparison?.value,
        })

        const processedChartOptions = processChartData(
          chartData,
          defaultChartOptions,
          selectedComparison,
          typeFilter
        )

        setLineChartOptions(processedChartOptions)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
        toast({
          title: 'Error fetching chart metrics',
          description:
            err instanceof Error ? err.message : 'An unknown error occurred',
          variant: 'destructive',
        })
      }
    }
  }, [
    dateRange?.from?.toISOString(),
    dateRange?.to?.toISOString(),
    intervalValue,
    selectedBranch,
    selectedComparison?.type,
    selectedComparison?.value,
    typeFilter,
  ])

  useEffect(() => {
    setLoading(true)
    setError(null)

    const fetchAllData = async () => {
      try {
        await fetchChartData()
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      } finally {
        setLoading(false)
      }
    }

    fetchAllData()
  }, [fetchChartData])

  return { loading, error }
}

function processChartData(
  chartData: any,
  defaultChartOptions: EChartsOption,
  selectedComparison: ComparisonSelection | null | undefined,
  typeFilter: string
): EChartsOption {
  const newChartData = {
    ...defaultChartOptions,
    ...chartData,
  }

  // When comparing metrics (comma-separated), apply per-series formatters
  if (selectedComparison?.type === 'metric') {
    const metricsRaw = selectedComparison?.value ?? ''
    const metrics = metricsRaw
      .split(',')
      .map((m) => m.trim())
      .filter((m) => m.length > 0)

    // Map each metric to its format kind
    const metricToFormatKind: Record<
      string,
      'money' | 'percentage' | 'number'
    > = {}
    metrics.forEach((metric) => {
      if (DOLLAR_METRICS.includes(metric)) {
        metricToFormatKind[metric] = 'money'
      } else if (PERCENTAGE_METRICS.includes(metric)) {
        metricToFormatKind[metric] = 'percentage'
      } else {
        metricToFormatKind[metric] = 'number'
      }
    })

    // Build a tooltip formatter that formats each series based on its metric type
    const tooltipFormatter = (params: any) => {
      const items = Array.isArray(params) ? params : [params]
      return items
        .map((p: any, idx: number) => {
          // Prefer seriesName to match metric name; fallback to index
          const seriesName: string = p.seriesName ?? metrics[idx] ?? ''
          const formatKind =
            metricToFormatKind[seriesName] ??
            metricToFormatKind[metrics[idx] as string] ??
            'number'
          const formatted = formatNumber(p.value, formatKind)
          return `${p.marker}${seriesName}: ${formatted}`
        })
        .join('<br/>')
    }

    return {
      ...newChartData,
      tooltip: {
        ...newChartData.tooltip,
        // axis trigger returns an array of series at that x
        formatter: tooltipFormatter,
        // Remove global valueFormatter to avoid overriding our per-series logic
        valueFormatter: undefined,
      } as any,
      // Optionally, add series-level label formatters if series are present in the returned options
      series: Array.isArray((newChartData as any).series)
        ? (newChartData as any).series.map((s: any, idx: number) => {
            const seriesName: string = s.name ?? metrics[idx] ?? ''
            const formatKind =
              metricToFormatKind[seriesName] ??
              metricToFormatKind[metrics[idx] as string] ??
              'number'
            return {
              ...s,
              label: {
                ...(s.label ?? {}),
                show: s.label?.show ?? false,
                formatter: (p: any) => formatNumber(p.value, formatKind),
              },
            }
          })
        : (newChartData as any).series,
    }
  }

  if (DOLLAR_METRICS.includes(typeFilter)) {
    const formatter = (value: any) => {
      if (typeof value === 'number') {
        return formatNumber(value, 'money')
      }
      return value
    }
    return {
      ...newChartData,
      tooltip: {
        ...newChartData.tooltip,
        valueFormatter: formatter,
      },
    }
  } else if (PERCENTAGE_METRICS.includes(typeFilter)) {
    const formatter = (value: any) => {
      if (typeof value === 'number') {
        return formatNumber(value, 'percentage')
      }
      return value
    }
    return {
      ...newChartData,
      tooltip: {
        ...newChartData.tooltip,
        valueFormatter: formatter,
      },
    }
  } else {
    return newChartData
  }
}
